import log from '@/lib/logger';
import type { AppDispatch } from '@/store';
import type { TrackerLog } from '@/types/swagger';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { http } from 'config';
import { callNextPage } from 'util/callNextPage';
import { getHumanizedAddress } from 'util/mapboxService';

// ----------------------------------- Actions ---------------------------------
// Action Types
const LOAD_LAST_TRACKER_LOG = 'tracker_logs/load_last_tracker_log';
const LOAD_VEHICLE_TRACKER_LOGS = 'tracker_logs/load_vehicle_tracker_logs';
const LAST_TRACKER_LOG_FETCH_START = 'tracker_logs/last_tracker_log_fetch_start';
const LAST_TRACKER_LOG_FETCH_END = 'tracker_logs/last_tracker_log_fetch_end';
const TRACKER_LOGS_FETCH_START = 'tracker_logs/tracker_logs_fetch_start';
const TRACKER_LOGS_FETCH_END = 'tracker_logs/tracker_logs_fetch_end';

export const loadLastTrackerLog = (number_plate: string, trackerLog: TrackerLog) => ({
  type: LOAD_LAST_TRACKER_LOG,
  meta: { number_plate },
  payload: trackerLog,
});
export const loadVehicleTrackerLogs = (
  number_plate: string,
  trackerLogs: TrackerLog[],
  override?: boolean
) => ({
  type: LOAD_VEHICLE_TRACKER_LOGS,
  meta: { number_plate, override },
  payload: trackerLogs,
});
export const lastTrackerLogFetchStart = () => ({
  type: LAST_TRACKER_LOG_FETCH_START,
});
export const lastTrackerLogFetchEnd = () => ({
  type: LAST_TRACKER_LOG_FETCH_END,
});
export const trackerLogsFetchStart = () => ({ type: TRACKER_LOGS_FETCH_START });
export const trackerLogsFetchEnd = () => ({ type: TRACKER_LOGS_FETCH_END });

// ----------------------------------- Reducer ---------------------------------
interface TracksLogsState {
  byId: Record<number, any>;
  byVehicleNumberPlate: Record<string, any>;
  allIds: number[];
  trackerLogsIsFetching: boolean;
  lastTrackerLogIsFetching: boolean;
}
// Init State
const INIT_STATE: TracksLogsState = {
  byId: {},
  byVehicleNumberPlate: {},
  allIds: [],
  trackerLogsIsFetching: false,
  lastTrackerLogIsFetching: false,
};

// Reducer
const trackerLogReducer = (state = INIT_STATE, action: UnknownAction): TracksLogsState => {
  const { type, meta, payload } = action as PayloadAction<TrackerLog | TrackerLog[]> & {
    meta: { number_plate: string; override: boolean };
  };
  switch (type) {
    case LOAD_LAST_TRACKER_LOG: {
      return {
        ...state,
        byVehicleNumberPlate: {
          ...state.byVehicleNumberPlate,
          [meta.number_plate]: {
            ...state.byVehicleNumberPlate[meta.number_plate],
            last: payload,
          },
        },
      };
    }
    case LOAD_VEHICLE_TRACKER_LOGS: {
      const prevTrips = state.byVehicleNumberPlate[meta.number_plate].trip
        ? [...state.byVehicleNumberPlate[meta.number_plate].trip]
        : [];
      const trip = meta.override ? payload : [...prevTrips, ...(payload as TrackerLog[])];

      return {
        ...state,
        byVehicleNumberPlate: {
          ...state.byVehicleNumberPlate,
          [meta.number_plate]: {
            ...state.byVehicleNumberPlate[meta.number_plate],
            trip,
          },
        },
      };
    }
    case LAST_TRACKER_LOG_FETCH_START: {
      return { ...state, lastTrackerLogIsFetching: true };
    }
    case LAST_TRACKER_LOG_FETCH_END: {
      return { ...state, lastTrackerLogIsFetching: false };
    }
    case TRACKER_LOGS_FETCH_START: {
      return { ...state, trackerLogsIsFetching: true };
    }
    case TRACKER_LOGS_FETCH_END: {
      return { ...state, trackerLogsIsFetching: false };
    }
    default: {
      return state;
    }
  }
};

export default trackerLogReducer;

export const fetchVehicleTrackerLogs =
  (number_plate: string, timestamp_start: string, timestamp_stop: string) =>
  async (dispatch: AppDispatch) => {
    const apiTrackerLogs = `telemetry/tracker_log/by_vehicle/${number_plate}/between/${timestamp_start}/${timestamp_stop}/?page_size=999`;
    dispatch(trackerLogsFetchStart());
    const resolve = (data: TrackerLog[]) => {
      if (data) dispatch(loadVehicleTrackerLogs(number_plate, data));
      dispatch(trackerLogsFetchEnd());
      return data;
    };
    const reject = (error: AxiosError) => {
      log.error(error);
      dispatch(trackerLogsFetchEnd());
      return error;
    };
    return callNextPage(apiTrackerLogs, resolve, reject);
  };

export const fetchLastVehicleTrackerLog =
  (number_plate: string) => async (dispatch: AppDispatch) => {
    const apiTrackerLogs = `telemetry/tracker_log/by_vehicle/${number_plate}/?page_size=1`;
    dispatch(lastTrackerLogFetchStart());
    return http
      .get(apiTrackerLogs)
      .then((response) => {
        if (response.data.features) {
          const lastTrackerLog = response.data.features[0];
          getHumanizedAddress(lastTrackerLog).then((placeName) => {
            lastTrackerLog.place_name = placeName;
            dispatch(loadLastTrackerLog(lastTrackerLog.properties.vehicle, lastTrackerLog));
          });
        }
        dispatch(lastTrackerLogFetchEnd());
        return response.data.results;
      })
      .catch((error) => {
        log.error(error);
        dispatch(lastTrackerLogFetchEnd());
        return error;
      });
  };

export const fetchTrackerLog = (trackerLogId: number) => async (dispatch: AppDispatch) => {
  const apiTrackerLogs = `telemetry/tracker_log/${trackerLogId}/?page_size=1`;
  dispatch(lastTrackerLogFetchStart());
  return http
    .get(apiTrackerLogs)
    .then((response) => {
      if (response.data) {
        const lastTrackerLog = response.data;
        getHumanizedAddress(lastTrackerLog).then((placeName) => {
          lastTrackerLog.place_name = placeName;
          dispatch(loadLastTrackerLog(lastTrackerLog.properties.vehicle, lastTrackerLog));
        });
      }
      dispatch(lastTrackerLogFetchEnd());
      return response.data;
    })
    .catch((error) => {
      log.error(error);
      dispatch(lastTrackerLogFetchEnd());
      return error;
    });
};
